import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { ISignUpRequest } from 'data-transfers/requests'
import { RecaptchaAction } from 'enums'

import useSettingsStore, { ActionType } from 'store/useSettingsStore'
import { useSignUp } from 'hooks/useAuth'
import useAuthManager from 'hooks/useAuthManager'

import { envUtil } from 'utils'
import { signUpEvent } from 'utils/gtm.util'
import { UserRole } from 'constants/auth'

import { IAuthValues, IFormSignUpProps, TabType } from './types'

const useFormSignUp = ({
  onSuccess,
  type
}: Pick<IFormSignUpProps, 'onSuccess' | 'type'>) => {
  const { t } = useTranslation(['actions', 'auth', 'inputs'])
  const [accType, setAccType] = useState<TabType | null>(type ?? null)
  const [showReCaptcha, setShowReCaptcha] = useState(false)
  const { hasAction } = useSettingsStore((state) => state)
  const [submittionCount, setSubmittionCount] = useState(0)
  const { signOut } = useAuthManager()
  const { captchaKey } = envUtil.getEnv()
  const recaptchaContainer = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const { mutate, isLoading, error, reset } = useSignUp({
    onSuccess: async (_, data) => {
      toast.success(t('toast.registerDone', { ns: 'auth' }))
      signOut()
      if (data.type) {
        signUpEvent(data.type)
      }
      onSuccess?.()
      router.push('/finish-registration')
    },
    onError: (error: any) => {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message + ' ' + t('actions:issues'))
      } else {
        toast.error(`Registration failed. ` + t('actions:issues'))
      }
    }
  })

  const changeTab = useCallback(
    (value: TabType) => {
      reset()
      setAccType(value)
    },
    [reset]
  )
  const finishRegistration = useCallback(
    (values: ISignUpRequest) => {
      mutate(values)

      setTimeout(() => {
        setShowReCaptcha(false)
        setSubmittionCount((val) => val + 1)
      }, 1000)
    },
    [mutate]
  )
  const onSubmit = useCallback(
    (values: IAuthValues) => {
      setShowReCaptcha(true)

      if (recaptchaContainer.current) {
        window.grecaptcha.render(recaptchaContainer.current, {
          sitekey: captchaKey,
          action: RecaptchaAction.SignUp,
          callback: (token) => {
            finishRegistration({
              ...values,
              token,
              action: RecaptchaAction.SignUp,
              type:
                accType === 'Candidate'
                  ? UserRole.Candidate
                  : UserRole.Organisation
            })
          }
        })
      }
    },
    [accType, captchaKey, finishRegistration]
  )

  useEffect(() => {
    if (hasAction === ActionType.Apply) {
      setAccType('Candidate')
      return
    }
    if (router.pathname.includes('org')) {
      setAccType('Organisation')
    } else if (router.pathname.includes('user')) {
      setAccType('Candidate')
    }
  }, [hasAction, router.pathname])

  return {
    submittionCount,
    onSubmit,
    isLoading,
    error,
    accType,
    hasAction,
    recaptchaContainer,
    showReCaptcha,
    changeTab
  }
}

export default useFormSignUp
